import {
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
} from '@chakra-ui/react';
import { garpApi } from '@texas/api/endpoints/erp/garpApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SharedDisclosureProps } from '@texas/components/shared/types';
import { createContext, useCallback, useEffect } from 'react';
import { useGarpCellForm } from './matrix/useGarpCellForm';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useTranslation } from 'react-i18next';
import { useGarpStandardForm } from './standard/useGarpStandardForm';
import { GarpCellCompareView } from './matrix/GarpCellCompareView';
import { GarpStandardCompareView } from './standard/StandardCompareView';

export const GarpCellFormContext = createContext<ReturnType<
  typeof useGarpCellForm
> | null>(null);

export const GarpFormContext = createContext<ReturnType<
  typeof useGarpStandardForm
> | null>(null);

interface Props extends SharedDisclosureProps {
  productApprovalId?: number;
  compositionGroupId: number;
  variantId: number;
  branchId: number;
}

export function SetGarpDataModal(props: Props) {
  return (
    <Modal
      size="6xl"
      isOpen={props.isOpen}
      onClose={props.onClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton zIndex={10} />
        <Inner
          productApprovalId={props.productApprovalId}
          compositionGroupId={props.compositionGroupId}
          variantId={props.variantId}
          branchId={props.branchId}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  compositionGroupId,
  variantId,
  branchId,
  productApprovalId,
}: {
  compositionGroupId: number;
  variantId: number;
  branchId: number;
  productApprovalId?: number;
}) {
  const fetchData = useCallback(() => {
    if (productApprovalId) {
      return garpApi.garpFriendlyPaData(compositionGroupId, productApprovalId);
    } else {
      return garpApi.garpFriendlyData(compositionGroupId);
    }
  }, [compositionGroupId, productApprovalId]);

  const { data, refetch, loading } = useApiResource(fetchData);

  useEffect(() => {
    if (compositionGroupId) {
      refetch();
    }
  }, [compositionGroupId, refetch]);

  const { t } = useTranslation();

  return (
    <>
      <ModalBody overflowY="auto" maxH="80vh" pt={0}>
        {loading && <SpinnerWithLabel label={t('general.loadingData')} />}

        {data && (
          <>
            {data.matrixNodeXValue ? (
              <GarpCellCompareView data={data} />
            ) : (
              <GarpStandardCompareView
                data={data}
                variantId={variantId}
                branchId={branchId}
              />
            )}
          </>
        )}
      </ModalBody>
    </>
  );
}
